<template>
  <div>
    <page-header
      :page="headerPage"
      :actions="headerActions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <board class="mb-4" :details="boardDetails">
      <template #data-item="{ item }">
        <div v-if="!item.data">
          <custom-balloon
            :background-color="item.statusColor"
            :text="item.statusName"
            dot
          />
        </div>
      </template>
    </board>
    <requisition-summary :summary="summary" />
    <div class="row my-3 mx-0">
      <div class="col-12">
        <custom-separator text="Equipos Pendientes de Requisicion" />
      </div>
    </div>
    <div class="row mr-3 mb-3" v-if="hasPendingRequisition">
      <div class="col-12">
        <checkbox
          :checked-bg-color="'bg-success'"
          :icon="'fal fa-check'"
          text="Seleccionar Todo"
          class="d-flex justify-content-end"
          v-model="selectAll"
          @input="selectAllItems()"
        />
      </div>
    </div>
    <validation-observer ref="requisitionForm">
      <custom-result-not-found
        v-if="!hasPendingRequisition"
        text="No hay Equipos Pendientes de Requisición"
      />
      <div v-else>
        <div class="row" v-for="(item, index) in details" :key="index">
          <div class="col-12">
            <custom-callout class="mb-3 custom-callout">
              <div class="row border-bottom py-3 m-0">
                <div class="col-md-3 col-12">
                  <div
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Equipo:
                  </div>
                  <div class="color-black font-16 font-weight-bold">
                    {{ item.equipmentName | empty }}
                  </div>
                </div>
                <div class="col-md-3 col-12">
                  <div
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Articulo de SIFDE:
                  </div>
                  <div class="color-black font-16 font-weight-normal">
                    <validation-provider
                      v-slot="{ errors }"
                      mode="eager"
                      :rules="item.checked ? 'required' : ''"
                    >
                      <custom-drop-down
                        v-model="item.itemSifde"
                        :clearable="false"
                        fieldkey="id"
                        fieldname="name"
                        :options="getSifdeEquipmentOptions(item)"
                        placeholder="Seleccione"
                        custom-error-msg="El campo es requerido"
                        :error="errors[0]"
                        :disabled="!item.checked"
                      />
                    </validation-provider>
                  </div>
                </div>
                <div class="col-12 col-lg-6">
                  <div class="color-black font-16 font-weight-normal">
                    <checkbox
                      checked-bg-color="bg-success"
                      icon="fal fa-check"
                      class="d-flex justify-content-end check-box__extended"
                      v-model="item.checked"
                      @input="selectItem(item)"
                    />
                  </div>
                </div>
              </div>
              <div class="row py-3 m-0">
                <div class="col-lg-3 col-12">
                  <div
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Tipo de Requisicion:
                  </div>
                  <div class="color-black font-16 font-weight-normal">
                    <validation-provider
                      v-slot="{ errors }"
                      mode="eager"
                      :rules="item.checked ? 'required' : ''"
                    >
                      <custom-drop-down
                        v-model="item.requisitionType"
                        :clearable="false"
                        fieldkey="id"
                        fieldname="name"
                        :options="sources.requisitionTypes"
                        placeholder="Seleccione"
                        custom-error-msg="El campo es requerido"
                        :error="errors[0]"
                        :disabled="!item.checked"
                      />
                    </validation-provider>
                  </div>
                </div>
                <div class="col-lg-2 col-12">
                  <div
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Cantidad:
                  </div>
                  <div class="color-black font-16 font-weight-normal">
                    <validation-provider
                      v-slot="{ errors }"
                      mode="eager"
                      :custom-messages="{
                        required: 'El campo es requerido.',
                      }"
                      :rules="item.checked ? 'required|greaterThanZero' : ''"
                    >
                      <custom-input
                        v-model="item.quantity"
                        type="number"
                        maxlength="9"
                        :error="errors[0]"
                        :disabled="!item.checked"
                        :min="0"
                      />
                    </validation-provider>
                  </div>
                </div>
                <div class="col-lg-2 col-12">
                  <div
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Unidad de Medida:
                  </div>
                  <div class="color-black font-16 font-weight-normal">
                    <validation-provider
                      v-slot="{ errors }"
                      mode="eager"
                      :rules="item.checked ? 'required' : ''"
                    >
                      <custom-drop-down
                        v-model="item.unitOfMeasure"
                        :clearable="false"
                        fieldkey="id"
                        fieldname="name"
                        :options="sources.units"
                        placeholder="Seleccione"
                        :error="errors[0]"
                        :disabled="!item.checked"
                      />
                    </validation-provider>
                  </div>
                </div>
                <div class="col-lg-2 col-12">
                  <div
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Precio:
                  </div>
                  <div class="color-black font-16 font-weight-normal">
                    <validation-provider
                      v-slot="{ errors }"
                      mode="eager"
                      :custom-messages="{
                        required: 'El campo es requerido.',
                      }"
                      :rules="item.checked ? 'required|greaterThanZero' : ''"
                    >
                      <money
                        class="form-control"
                        v-model="item.price"
                        maxlength="500"
                        :error="errors[0]"
                        :min="0"
                        :disabled="!item.checked"
                      />
                    </validation-provider>
                  </div>
                </div>
                <div class="col-lg-3 col-12">
                  <div
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Cifra de Cuenta:
                  </div>
                  <div class="color-black font-16 font-weight-normal">
                    <validation-provider
                      v-slot="{ errors }"
                      mode="eager"
                      :rules="item.checked ? 'required' : ''"
                    >
                      <custom-drop-down
                        v-model="item.purchaseOrder"
                        :clearable="false"
                        fieldkey="id"
                        fieldname="name"
                        :options="sources.purchaseOrderList"
                        placeholder="Seleccione"
                        custom-error-msg="El campo es requerido"
                        :error="errors[0]"
                        :disabled="!item.checked"
                      />
                    </validation-provider>
                  </div>
                </div>
              </div>
              <template #edition>
                <div class="bg-light-gray">
                  <custom-tab nav-class="nav-vue-underline" no-nav-style>
                    <custom-tab-item title="Descripción del Equipo">
                      <div class="p-4">
                        {{ item.description }}
                      </div>
                    </custom-tab-item>
                  </custom-tab>
                </div>
              </template>
            </custom-callout>
          </div>
        </div>
      </div>
      <section-title
        content="Documentos Adicionales"
        has-line
        v-if="hasPendingRequisition"
      />
      <div
        class="document-row row justify-content-start bg-gray p-4 m-0 mb-3"
        v-if="audiologicFileData.id && hasPendingRequisition"
      >
        <div class="col-md-4 col-12">
          <div class="color-secondary-6 font-14 font-weight-normal mr-2">
            Nombre del Documento:
          </div>
          <div class="color-black font-16">Evaluación Audiológica</div>
        </div>
        <div class="col-md col-12">
          <div class="color-secondary-6 font-14 font-weight-normal mr-2">
            Archivo:
          </div>
          <div class="color-black font-16">
            <custom-file-input
              class="btn-action-size"
              i="fas fa-file-download"
              v-model="audiologicFileData.model"
              variant="success"
              accept=".pdf"
              :on-download="downloadHandler"
              disabled
              display-style="normal"
              :name="audiologicFileData.name"
              :attachment-id="audiologicFileData.id"
            />
          </div>
        </div>
      </div>
      <div
        class="document-row row justify-content-start bg-gray p-4 m-0 mb-3"
        v-if="hasPendingRequisition"
      >
        <div class="col-md-4 col-12">
          <div class="color-secondary-6 font-14 font-weight-normal mr-2">
            Nombre del Documento:
          </div>
          <div class="color-black font-16">Hoja de Cotización</div>
        </div>
        <div class="col-md-3 col-12">
          <div class="color-secondary-6 font-14 font-weight-normal mr-2">
            Archivo:
          </div>
          <div class="color-black font-16">
            <custom-file-input
              class="btn-action-size"
              v-model="quoteSheetFile"
              i="fas fa-file-download"
              variant="success"
              accept=".pdf"
              display-style="normal"
              :on-download="downloadHandler"
              ref="quoteSheetFile"
            />
          </div>
        </div>
      </div>
      <div
        class="document-row row justify-content-start bg-gray p-4 m-0"
        v-if="hasPendingRequisition"
      >
        <div class="col-md-4 col-12">
          <div class="color-secondary-6 font-14 font-weight-normal mr-2">
            Nombre del Documento:
          </div>
          <div class="color-black font-16">Otros</div>
        </div>
        <div class="col-md-3 col-12">
          <div class="color-secondary-6 font-14 font-weight-normal mr-2">
            Archivo:
          </div>
          <div class="color-black font-16">
            <custom-file-input
              class="btn-action-size"
              v-model="otherAtFile"
              ref="otherAtFile"
              i="fas fa-file-download"
              variant="success"
              accept=".pdf"
              display-style="normal"
              :on-download="downloadHandler"
            />
          </div>
        </div>
        <div
          class="col-md-6 col-12 justify-content-end align-items-center d-flex"
        ></div>
      </div>
    </validation-observer>
    <div class="my-3 ">
      <custom-separator text="Listado de Requisiciones Realizadas" />
      <div v-if="hasRequisitions" class="px-3">
        <div v-for="(item, index) in requisitions" :key="index">
          <requisition-row
            :item="item"
            :index="index"
            :pei-at-service-id="peiAtServiceId"
            @reload="init()"
          ></requisition-row>
        </div>
      </div>
      <custom-result-not-found v-else text="No hay Requisiciones Realizadas" />
    </div>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import CustomSeparator from 'custom-separator';
import Checkbox from 'CheckBox';
import CustomCallout from 'custom-callout';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import CustomResultNotFound from 'custom-result-not-found';
import RequisitionSummary from './RequisitionSummary.vue';
import RequisitionRow from './RequisitionRow.vue';
import { date } from '@/utils/filters';
import referralService from '@/services/api/referralsandequipments.api';
import apiService from '@/services/api/requisitionmanagment.api';
import CustomBalloon from 'custom-balloon';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import { serialize as objectToFormData } from 'object-to-formdata';
import mixAlert from '@/mixins/alert.mixin';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import SectionTitle from 'SectionTitle';
import CustomFileInput from 'CustomFileInput';
import documentService from '@/services/api/document.api.js';
import documentTypes from '@/utils/constants/documentTypes';
import hubservice from '@/services/api/hub/hubservice.js';
import Vue from 'vue';
import { getSysparameterByNames } from '@/services/api/sysparameter.api';
import { Money } from 'v-money';

const RETURN_ROUTE = '/at/requisitionmanagement';

export default {
  name: 'RequisitionDetail',
  components: {
    PageHeader,
    Board,
    CustomSeparator,
    RequisitionSummary,
    Checkbox,
    CustomCallout,
    CustomDropDown,
    CustomInput,
    CustomResultNotFound,
    CustomBalloon,
    CustomTab,
    CustomTabItem,
    RequisitionRow,
    ValidationProvider,
    ValidationObserver,
    SectionTitle,
    CustomFileInput,
    Money,
  },
  mixins: [mixAlert],
  data: () => ({
    referral: {},
    details: [],
    selectAll: false,
    sources: {},
    requisitions: [],
    summaryKey: 0,
    audiologicFileData: {
      model: null,
      documentId: null,
      name: null,
    },
    quoteSheetFile: null,
    otherAtFile: null,
    selectAllIgnoreCallback: false,
    isInventoryFlowEnabled: false,
    requisitionInventoryFlow: false,
  }),
  computed: {
    peiAtServiceId() {
      return Number(this.$route.params.peiAtServiceId);
    },
    hasPendingRequisition() {
      return this.details.length > 0;
    },
    hasRequisitions() {
      return this.requisitions.length > 0;
    },
    headerPage() {
      return {
        icon: 'fas fa-list',
        title: 'Requisición',
        routes: [
          'Asistencia Tecnológica',
          'Manejo de Requisiciones',
          'Añadir Requisición',
        ],
      };
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.push(RETURN_ROUTE),
        },
        {
          text: 'Enviar Requisición',
          icon: 'fas fa-arrow-circle-right',
          variant: 'success',
          visible: this.hasPendingRequisition,
          click: () => this.send(),
        },
      ];
    },
    boardDetails() {
      return [
        [
          {
            label: 'Estudiante:',
            icon: 'fas fa-user',
            data: `${this.referral.studentName} - ${this.referral.studentSie}`,
            cols: 'col-12 col-md-3 pt-2',
          },
          {
            label: 'Número de Referido:',
            icon: 'fas fa-file-user',
            data: this.referral.referralNumber || '-',
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'Fecha de Referido:',
            icon: 'fas fa-calendar-minus',
            data: date(this.referral.referralDate),
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'CAAT:',
            icon: 'fas fa-location-dot',
            data: this.referral.caat || '-',
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-file-lines',
            statusColor: this.referral.serviceStatusColor,
            statusName: this.referral.serviceStatusName,
            cols: 'col-12 col-md-3 pt-2',
          },
        ],
      ];
    },
    summary() {
      const reqs = this.requisitions;
      const checked = this.details.filter((x) => x.checked);
      return {
        total: reqs.length,
        requisitioned: reqs.reduce((sum, { detail }) => sum + detail.length, 0),
        pending: this.details.length,
        toRequisition: checked.length,
        totalAmount: checked.reduce((total, { price }) => total + price, 0),
      };
    },
    getSifdeEquipmentOptions() {
      return (item) => {
        if (item.sifdeEquipmentOptions) return item.sifdeEquipmentOptions;
        return this.sources.sifdeEquipmentList;
      };
    },
  },
  created() {
    Vue.use(hubservice);
    this.$userHub.$on('hub-requisition-update', this.requisitionUpdatedByHub);
  },
  beforeDestroy() {
    //clean SignalR event
    this.$userHub.$off('hub-requisition-update', this.requisitionUpdatedByHub);

    /* eslint-disable no-debugger, no-console */
    console.log("hub-requisition-update------------off'");
  },
  async mounted() {
    await this.getSources();
    await this.getAudiologicalEvaluation();
    await this.init();
    await this.getInventoryParam();
    await this.getConfSideEquipments();
  },
  methods: {
    async requisitionUpdatedByHub() {
      await this.getRequisitions();
    },
    async init() {
      await this.getBoardInfo();
      await this.getInfo();
      await this.getRequisitions();
    },
    async getBoardInfo() {
      const { data } = await referralService.getReferralSummary(
        this.peiAtServiceId
      );
      this.referral = data;
    },
    async getInfo() {
      this.selectAll = false;
      const { data } = await apiService.getRequisitionDetail(
        this.peiAtServiceId
      );
      this.details = data.map((x) => ({
        ...x,
        price: 0,
        checked: false,
        sifdeEquipmentOptions: null,
      }));
    },
    async getRequisitions() {
      const { data } = await apiService.getRequisitions(this.peiAtServiceId);
      this.requisitions = data.map((x) => ({
        ...x,
        expanded: false,
        checked: false,
        selected: false,
        documents: [],
      }));
    },
    async getSources() {
      const { data } = await apiService.getRequisitionSources();
      this.sources = data;
    },
    async getAudiologicalEvaluation() {
      const { data } = await apiService.getAudiologicalEvaluation(
        this.peiAtServiceId
      );
      this.audiologicFileData = data;
    },
    async downloadHandler() {
      await documentService
        .downloadFromServer(this.audiologicFileData.id)
        .catch(() => {
          this.ShowToast(
            'Error',
            'Hubo un error al descargar el documento',
            'error'
          );
        });
    },
    selectAllItems() {
      if (this.selectAllIgnoreCallback) return;
      this.details.forEach((item) => (item.checked = this.selectAll));
    },
    selectItem({ checked }) {
      if (checked) {
        const every = this.details.every((x) => x.checked);
        if (every) this.selectAll = true;
        this.$refs.requisitionForm.reset();
      } else if (this.selectAll) {
        this.selectAllIgnoreCallback = true;
        this.selectAll = false;
        setTimeout(() => (this.selectAllIgnoreCallback = false), 100);
      }
    },
    async send() {
      const hasSelection = this.details.some((x) => x.checked);

      if (!hasSelection) {
        this.ShowToast(
          'Alerta',
          'Por favor de seleccionar al menos un equipo.',
          'error'
        );
        return;
      }

      const isValid = await this.$refs.requisitionForm.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Por favor completa los campos requeridos.',
          'error'
        );
        return;
      }

      const isConfirmed = await this.ShowCancelModal(
        'Confirmación',
        `Certifico que la información ingresada es correcta para realizar la requisición del(los) equipo(s) asistivo(s)`,
        'Sí',
        'No'
      );

      if (!isConfirmed) {
        return;
      }

      const model = {
        peiAtServiceId: this.peiAtServiceId,
        detail: [],
        documents: [
          this.quoteSheetFile
            ? {
                documentTypeId: documentTypes.QuoteSheet,
                file: this.quoteSheetFile,
              }
            : null,
          this.otherAtFile
            ? {
                documentTypeId: documentTypes.OtherAt,
                file: this.otherAtFile,
              }
            : null,
        ],
      };

      model.detail = this.details
        .filter((x) => x.checked)
        .map((r) => {
          return {
            peiAtServiceRecomendationId: r.equipmentId,
            isSIFDERegular: r.requisitionType.id == 1,
            isSIFDEAunction: r.requisitionType.id == 2,
            quantity: r.quantity,
            price: r.price,
            distributionLine: '1',
            unit: r.unitOfMeasure.id,
            purcharseOrderId: r.purchaseOrder.id,
            ItemSifdeId: r.itemSifde.id,
          };
        });

      const formData = objectToFormData(model, {
        indices: true,
        nullsAsUndefineds: true,
        dotsForObjectNotation: true,
      });

      try {
        const { data } = await apiService.saveRequisition(formData);
        if (data.success) {
          this.ShowToast('Notificación', data.message, 'warning');
        } else {
          this.ShowToast('Alerta', data.message, 'error');
        }
      } catch (error) {
        this.ShowToast('Alerta', error?.response?.data.message, 'error');
      } finally {
        this.$refs.quoteSheetFile.removeFile(new Event('quoteSheetFile'));
        this.$refs.otherAtFile.removeFile(new Event('otherAtFile'));
        await this.init();
      }
    },
    async getInventoryParam() {
      const { data } = await getSysparameterByNames({
        parameterNames: [
          'TechAssistanceServicesInventoryFlow',
          'TechAssistanceServicesRequisitionInventoryFlow',
        ],
      });

      this.isInventoryFlowEnabled =
        data.find((x) => x.name == 'TechAssistanceServicesInventoryFlow')
          .staticValue &&
        data
          .find((x) => x.name == 'TechAssistanceServicesInventoryFlow')
          .staticValue.toLowerCase() === 'true';

      this.requisitionInventoryFlow =
        data.find(
          (x) => x.name == 'TechAssistanceServicesRequisitionInventoryFlow'
        ).staticValue &&
        data
          .find(
            (x) => x.name == 'TechAssistanceServicesRequisitionInventoryFlow'
          )
          .staticValue.toLowerCase() === 'true';
    },
    async getConfSideEquipments() {
      if (!this.isInventoryFlowEnabled || !this.requisitionInventoryFlow)
        return;
      const equipmentIds = this.details
        .filter((x) => x.catalogId)
        .map((x) => x.equipmentId);
      const { data } = await apiService.getConfSideEquipments(equipmentIds);
      this.details.forEach((x) => {
        const item = data.find((y) => y.equipmentId === x.equipmentId);
        if (item) {
          x.sifdeEquipmentOptions = item.options;
          if (item.options.length === 1) {
            x.itemSifde = x.sifdeEquipmentOptions[0];
          }
        }
      });
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep .font-weight-bold {
  font-weight: 700 !important;
}

.document-row {
  border-radius: 0.5rem;
  border-left: 4px solid #b5c3bc;
}
</style>
